import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "reactstrap";
import { Checkbox, FormControlLabel } from "@mui/material";

//i18n
import { withTranslation } from "react-i18next";

import { usePremiseTypeQuery } from "apis/PremiseType";
import { useLazyZoneQuery } from "apis/Zone";
import { useLazyBlockQuery } from "apis/Block";
import { usePremiseAreaQuery } from "apis/PremiseArea";
import { useRoadQuery } from "apis/Road";
import { useLazyBuildingQuery } from "apis/Building";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import { applicationTypes, defaultCity, defaultPinCode } from "common/constant";
import {
  dcsConnectionTypeEnum,
  dcsIndividualUnitTypeEnum,
  getOptionValue,
} from "common/enums";

import { resetFieldValue } from "utils";

const AddressForPower = ({
  formik,
  floorOptions,
  isDisable = false,
  floorFieldProps = {},
  t,
}) => {
  const {
    values: { utilityAddress, applicationTypeId, utilityId, utilities },
    setFieldValue,
  } = formik;

  const {
    premiseTypeId,
    premisesAreaId,
    zoneName,
    blockName,
    roadName,
    buildingName,
    // isRural,
    isBPL,
    dcsDomesticPremiseType,
    dcsIndividualPremiseType,
  } = utilityAddress;

  const { data: { data: premiseTypes } = {} } = usePremiseTypeQuery({
    utilityId: utilityId ?? "",
  });
  const { data: { data: { items: premiseArea } = {} } = {} } =
    usePremiseAreaQuery();
  const [getZoneData, { data: { data: zones } = {} }] = useLazyZoneQuery();
  const [getBlockData, { data: { data: blocks } = {} }] = useLazyBlockQuery();
  const { data: { data: roads } = {} } = useRoadQuery();
  const [getBuildingData, { data: { data: building } = {} }] =
    useLazyBuildingQuery();

  const isShiftingOfServiceApplication = useMemo(
    () => applicationTypeId === applicationTypes["Shifting of Service"],
    [applicationTypeId]
  );

  const isResidential = useMemo(
    () =>
      premiseTypeId &&
      premiseTypes &&
      premiseTypeId ===
        premiseTypes?.find(
          (premise) => premise.name.toLowerCase() === "residential"
        )?.id,
    [premiseTypeId, premiseTypes]
  );

  const isDcsConnectionForVisible = useMemo(
    () =>
      utilities?.some(
        ({ value }) => value === getOptionValue("utilityOptions", "DCS")
      ) &&
      premiseTypes
        ?.find(({ id }) => id === premiseTypeId)
        ?.name.toLowerCase() === "domestic",
    [premiseTypes, premiseTypeId, utilities]
  );

  const isIndividualUnitTypeVisible = useMemo(
    () =>
      utilities?.some(
        ({ value }) => value === getOptionValue("utilityOptions", "DCS")
      ) &&
      dcsDomesticPremiseType ===
        getOptionValue("dcsConnectionTypeEnum", "Individual Units"),
    [dcsDomesticPremiseType]
  );

  useEffect(() => {
    if (premiseTypes) {
      setFieldValue("extraValues.premiseTypes", premiseTypes);
    }
  }, [premiseTypes]);

  useEffect(() => {
    if (premisesAreaId) {
      getZoneData(premisesAreaId);
    }
  }, [premisesAreaId, getZoneData]);

  // get blocks based on selected zone
  useEffect(() => {
    const zoneId = zones?.find((item) => item.name === zoneName)?.id;
    if (zoneId) {
      getBlockData(zoneId);
    } else if (zoneName) {
      setFieldValue("utilityAddress.zoneName", zoneName);
    }
  }, [zones, zoneName, getBlockData]);

  // get buildings based on selected block
  useEffect(() => {
    const blockId = blocks?.find((item) => item.name === blockName)?.id;
    if (blockId) {
      getBuildingData(blockId);
    } else if (blockName) {
      setFieldValue("utilityAddress.blockName", blockName);
    }
  }, [blocks, blockName, getBuildingData]);

  useEffect(() => {
    const roadId = roads?.find((item) => item.name === roadName)?.id;
    if (!roadId && roadName) {
      setFieldValue("utilityAddress.roadName", roadName);
    }
  }, [roads, roadName]);

  useEffect(() => {
    const buildingId = building?.find((item) => item.name === buildingName)?.id;
    if (!buildingId && buildingName) {
      setFieldValue("utilityAddress.buildingName", buildingName);
    }
  }, [building, buildingName]);

  useEffect(() => {
    if (isResidential) {
      // setFieldValue("utilityAddress.isRural", isRural || false);
      setFieldValue("utilityAddress.isBPL", isBPL || false);
    } else {
      // setFieldValue("utilityAddress.isRural", "");
      setFieldValue("utilityAddress.isBPL", "");
    }
  }, [isResidential]);

  useEffect(() => {
    if (zoneName === "other") {
      setFieldValue("utilityAddress.blockName", "other");
      setFieldValue("utilityAddress.buildingName", "other");
    }
  }, [zoneName]);

  useEffect(() => {
    if (blockName === "other") {
      setFieldValue("utilityAddress.buildingName", "other");
    }
  }, [blockName]);

  return (
    <Row>
      {!isShiftingOfServiceApplication && (
        <>
          <Col md={4}>
            <SelectField
              label={t("Premise Type") + "*"}
              name="utilityAddress.premiseTypeId"
              onChange={(_, { value }) => {
                setFieldValue("utilityAddress.premiseTypeId", value);
                if (!!dcsDomesticPremiseType || !!dcsIndividualPremiseType) {
                  resetFieldValue(setFieldValue, [
                    "utilityAddress.dcsDomesticPremiseType",
                    "utilityAddress.dcsIndividualPremiseType",
                  ]);
                }
              }}
              formik={formik}
              disabled={isDisable}
              options={premiseTypes || []}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
            />
          </Col>
          {isDcsConnectionForVisible && (
            <Col md={4}>
              <SelectField
                label={t("Dcs Connection For") + "*"}
                name="utilityAddress.dcsDomesticPremiseType"
                onChange={(_, { value }) => {
                  setFieldValue("utilityAddress.dcsDomesticPremiseType", value);
                  setFieldValue("utilityAddress.dcsIndividualPremiseType", "");
                }}
                formik={formik}
                disabled={isDisable}
                options={dcsConnectionTypeEnum}
              />
            </Col>
          )}
          {isIndividualUnitTypeVisible && (
            <Col md={4}>
              <SelectField
                label={t("Individual Units Type") + "*"}
                name="utilityAddress.dcsIndividualPremiseType"
                formik={formik}
                disabled={isDisable}
                options={dcsIndividualUnitTypeEnum}
              />
            </Col>
          )}
          <Col md={4}>
            <InputFieldText
              label={t("Premise Usage Purpose") + "*"}
              name="utilityAddress.premiseUsagePurpose"
              formik={formik}
              disabled={isDisable}
              placeholder="Enter Premise Usage Purpose"
            />
          </Col>
        </>
      )}
      <Col md={4}>
        <SelectField
          label={t("Premise Area") + "*"}
          name="utilityAddress.premisesAreaId"
          formik={formik}
          disabled={isDisable}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          options={premiseArea || []}
          onChange={(_, option) => {
            setFieldValue("utilityAddress.premisesAreaId", option.value);
            resetFieldValue(setFieldValue, [
              "utilityAddress.blockName",
              "utilityAddress.zoneName",
              "utilityAddress.buildingName",
              "utilityAddress.roadName",
            ]);
          }}
        />
      </Col>
      <Col md={4}>
        <SelectField
          label={t("Select Zone") + "*"}
          name="utilityAddress.zoneName"
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          formik={formik}
          disabled={!premisesAreaId || isDisable}
          options={[
            ...(zones || []),
            { id: 0, name: "other" },
            ...([...(zones || []), { id: 0, name: "other" }]?.find(
              (zoneObj) => zoneObj.name === zoneName
            )
              ? []
              : [{ id: 0, name: zoneName }]),
          ]}
          placeholder="Select Zone"
          onChange={(_, option) => {
            setFieldValue("utilityAddress.zoneName", option.value);
            resetFieldValue(setFieldValue, [
              "utilityAddress.blockName",
              "utilityAddress.buildingName",
              "utilityAddress.roadName",
            ]);
          }}
        />
      </Col>
      {utilityAddress.zoneName === "other" && (
        <Col md={4}>
          <InputFieldText
            label={t("Enter Zone") + "*"}
            name="utilityAddress.otherZone"
            formik={formik}
            placeholder="Enter Zone"
          />
        </Col>
      )}

      {zoneName !== "other" && (
        <Col md={4}>
          <SelectField
            label={t("Select Block") + "*"}
            name="utilityAddress.blockName"
            formik={formik}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
            disabled={!zoneName || isDisable}
            options={[
              ...(blocks || []),
              { id: 0, name: "other" },
              ...([...(blocks || []), { id: 0, name: "other" }]?.find(
                (blockObj) => blockObj.name === blockName
              )
                ? []
                : [{ id: 0, name: blockName }]),
            ]}
            placeholder="Select Block"
            onChange={(_, option) => {
              setFieldValue("utilityAddress.blockName", option.value);
              resetFieldValue(setFieldValue, [
                "utilityAddress.buildingName",
                "utilityAddress.roadName",
              ]);
            }}
          />
        </Col>
      )}

      {(zoneName === "other" || blockName === "other") && (
        <Col md={4}>
          <InputFieldText
            label={t("Enter Block") + "*"}
            name="utilityAddress.otherBlock"
            formik={formik}
            placeholder="Enter Block"
          />
        </Col>
      )}

      {
        <Col md={4}>
          <SelectField
            label={t("Select Road") + "*"}
            name="utilityAddress.roadName"
            formik={formik}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
            options={[
              ...(roads || []),
              { id: 0, name: "other" },
              ...([...(roads || []), { id: 0, name: "other" }]?.find(
                (roadObj) => roadObj.name === roadName
              )
                ? []
                : [{ id: 0, name: roadName }]),
            ]}
            disabled={!blockName || isDisable}
            placeholder="Select Road"
          />
        </Col>
      }

      {roadName === "other" && (
        <Col md={4}>
          <InputFieldText
            label={t("Enter Road") + "*"}
            name="utilityAddress.otherRoad"
            formik={formik}
            placeholder="Enter Road"
          />
        </Col>
      )}

      {zoneName !== "other" && blockName !== "other" && (
        <Col md={4}>
          <SelectField
            label={t("Select Building") + "*"}
            name="utilityAddress.buildingName"
            formik={formik}
            disabled={isDisable || !blockName}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
            options={[
              ...(building || []),
              { id: 0, name: "other" },
              ...([...(building || []), { id: 0, name: "other" }]?.find(
                (buildingObj) => buildingObj.name === buildingName
              )
                ? []
                : [{ id: 0, name: buildingName }]),
            ]}
            placeholder="Select Building"
          />
        </Col>
      )}

      {(zoneName === "other" ||
        buildingName === "other" ||
        blockName === "other") && (
        <Col md={4}>
          <InputFieldText
            label={t("Enter Building") + "*"}
            name="utilityAddress.otherBuilding"
            formik={formik}
            placeholder="Enter Building Name"
          />
        </Col>
      )}

      <Col md={4}>
        <SelectField
          label={t("Floor Number")}
          name="utilityAddress.floors"
          multiple
          disableCloseOnSelect
          disabled={
            applicationTypeId === applicationTypes["Shifting of Service"] ||
            applicationTypeId === applicationTypes["Net Metering"]
              ? isDisable
              : !buildingName
          }
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props} key={option.value}>
                <Checkbox sx={{ marginRight: "8px" }} checked={selected} />
                {option.label}
              </li>
            );
          }}
          formik={formik}
          onChange={(event, options) => {
            if (event.key !== "Backspace" && event.key !== "Delete") {
              setFieldValue("utilityAddress.floors", options);
            }
          }}
          value={utilityAddress.floors || []}
          options={floorOptions}
          placeholder="Select Floor Number"
          {...floorFieldProps}
        />
      </Col>
      <Col md={4}>
        <InputFieldText
          label={t("Unit ∕ Flat No") + "*"}
          name="utilityAddress.unitName"
          formik={formik}
          disabled={
            applicationTypeId === applicationTypes["Premise Amalgamation"] ||
            applicationTypeId === applicationTypes["Premise Removal"]
              ? ""
              : isDisable
          }
          placeholder="Enter Unit ∕ Flat No"
        />
      </Col>
      <Col md={4}>
        <InputFieldText
          label={t("Nearest Landmark")}
          name="utilityAddress.nearestLandmark"
          disabled={isDisable}
          formik={formik}
          placeholder="Enter Nearest Landmark"
        />
      </Col>
      <Col md={4}>
        <InputFieldText
          label={t("City")}
          formik={formik}
          disabled
          value={defaultCity}
        />
      </Col>
      <Col md={4}>
        <InputFieldText
          value={defaultPinCode}
          label={t("Pincode")}
          formik={formik}
          disabled
        />
      </Col>

      <Row>
        {!isShiftingOfServiceApplication && isResidential && (
          <>
            <Col md={4} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!isBPL}
                    disabled={isDisable}
                    onChange={() =>
                      setFieldValue("utilityAddress.isBPL", !isBPL)
                    }
                  />
                }
                label={t("BPL")}
              />
            </Col>

            {/* <Col md={4} xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!isRural}
                  onChange={() =>
                    setFieldValue("utilityAddress.isRural", !isRural)
                  }
                />
              }
              label="Rural"
            />
          </Col> */}
          </>
        )}
      </Row>
    </Row>
  );
};

AddressForPower.propTypes = {
  formik: PropTypes.object,
  floorOptions: PropTypes.array,
  t: PropTypes.func,
  isDisable: PropTypes.bool,
  floorFieldProps: PropTypes.object,
};

export default withTranslation()(AddressForPower);
